import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '../use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useGetSiteConfigs } from '../use-site-configs';
import { ApiBackendInfo } from '../athena/types';
import { GetMatchGroupsRequest, Group, GroupDate, MatchGroupDetail, MatchGroupNextGame, MatchGroup, GroupByMatchIdData, GroupByMatchIdsData } from './types';
import {
	mockGroupByMatchId,
	mockGroupDates,
	mockMatchGroups,
	mockGroups,
	mockMatchGroupDetail,
	mockMatchGroupNextGame,
	mockGroupByMatchIds
} from './mock-data';
const isMockEnabled = false;

/**
 * 下注後取得對應 Group 的派獎日期 (for 新下注進 My Tickets 預設查詢日期用)
 * @param apiBackendInfo 
 * @param matchId 
 * @returns DrawTime 開獎時間
 */
const fetchGroupByMatchId = async (apiBackendInfo: ApiBackendInfo, matchId: number): Promise<ApiResponse<GroupByMatchIdData>> => {
	if (isMockEnabled) {
		return await mockGroupByMatchId();
	}
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetGroupByMatchId?matchId=${matchId}`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 下注後取得對應 Group 的派獎日期 (for 新下注進 My Tickets 預設查詢日期用)
 * @param apiBackendInfo 
 * @param matchIds 
 * @returns GroupByMatchIdsData[]
 */
export const fetchGroupByMatchIds = async (apiBackendInfo: ApiBackendInfo, matchIds: number[]): Promise<ApiResponse<GroupByMatchIdsData[]>> => {
	if (isMockEnabled) {
		return await mockGroupByMatchIds();
	}
	const paras = matchIds.map(matchId => `matchIds=${matchId}`).join('&');
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetGroupByMatchIds?${paras}`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 取得所有 Group 日期選單資料 (專區各頁面預設查詢日期用)
 * @param apiBackendInfo 
 * @returns GroupDates[]
 */
const fetchGroupDates = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<GroupDate[]>> => {
	if (isMockEnabled) {
		return await mockGroupDates();
	}
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetGroupDates`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 取得 Group 資訊 (依據 filterType 篩選)
 * @param apiBackendInfo 
 * @param request 
 * @returns MatchGroup[]
 */
const fetchMatchGroups = async (apiBackendInfo: ApiBackendInfo, request: GetMatchGroupsRequest): Promise<ApiResponse<MatchGroup[]>> => {
	if (isMockEnabled) {
		return await mockMatchGroups();
	}
	const response = await fetchComposer.postWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetMatchGroups`, {
		body: JSON.stringify(request)
	})(apiBackendInfo.token);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 取得多筆 Group 資訊
 * @param apiBackendInfo 
 * @param groupIds 
 * @param matchIds 
 * @returns Group[]
 */
const fetchGroups = async (apiBackendInfo: ApiBackendInfo, groupIds: string[], matchIds: number[]): Promise<ApiResponse<Group[]>> => {
	if (isMockEnabled) {
		return await mockGroups();
	}
	const response = await fetchComposer.postWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetGroups`, {
		body: JSON.stringify({ groupIds, matchIds })
	})(apiBackendInfo.token);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 取得單一 Group 詳細資訊
 * @param apiBackendInfo 
 * @param groupId 
 * @returns MatchGroupDetail
 */
const fetchMatchGroupDetail = async (apiBackendInfo: ApiBackendInfo, groupId: string): Promise<ApiResponse<MatchGroupDetail>> => {
	if (isMockEnabled) {
		return await mockMatchGroupDetail();
	}
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetMatchGroupDetail?groupId=${groupId}`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 取得單一 Group 推薦賽事導轉資訊
 * @param apiBackendInfo 
 * @param groupId 
 * @returns MatchGroupNextGame
 */
const fetchMatchGroupNextGame = async (apiBackendInfo: ApiBackendInfo, groupId: string): Promise<ApiResponse<MatchGroupNextGame>> => {
	if (isMockEnabled) {
		return await mockMatchGroupNextGame();
	}
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetMatchGroupNextGame?groupId=${groupId}`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export const useGroupByMatchId = (matchId: number) => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	return useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_GROUP_BY_MATCH_ID,
			apiBackendInfo.url,
			matchId
		],
		queryFn: () => fetchGroupByMatchId(apiBackendInfo, matchId),
		enabled: Boolean(siteConfig?.EnableGoldenDuel) && apiBackendInfo.isReady && !!matchId,
		refetchOnWindowFocus: false
	});
};

export const useGroupByMatchIds = (matchIds: number[]) => {
	const apiBackendInfo = useApiBackendInfo();

	return useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_GROUP_BY_MATCH_ID,
			apiBackendInfo.url,
			matchIds.sort().join(',')
		],
		queryFn: () => fetchGroupByMatchIds(apiBackendInfo, matchIds),
		enabled: false,
		refetchOnWindowFocus: false
	});
};

export const useGroupDates = () => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	return useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_GROUP_DATES,
			apiBackendInfo.url
		],
		queryFn: () => fetchGroupDates(apiBackendInfo),
		enabled: Boolean(siteConfig?.EnableGoldenDuel) && apiBackendInfo.isReady,
		cacheTime: 30 * 1000, // 30 seconds
		refetchOnWindowFocus: false
	});
};

export const useMatchGroups = (request: GetMatchGroupsRequest) => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	return useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_MATCH_GROUPS,
			apiBackendInfo.url,
			apiBackendInfo.token ?? '',
			JSON.stringify(request)
		],
		queryFn: () => fetchMatchGroups(apiBackendInfo, request),
		enabled: Boolean(siteConfig?.EnableGoldenDuel) && apiBackendInfo.isReady,
		cacheTime: 30 * 1000, // 30 seconds
		refetchOnWindowFocus: false
	});
};

export const useGroups = (groupIds: string[], matchIds: number[]) => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	return useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_GROUPS,
			apiBackendInfo.url,
			groupIds.sort().join(','),
			matchIds.sort().join(',')
		],
		queryFn: () => fetchGroups(apiBackendInfo, groupIds, matchIds),
		enabled: Boolean(siteConfig?.EnableGoldenDuel) && apiBackendInfo.isReady,
		cacheTime: 60 * 1000, // 60 seconds
		staleTime: 60 * 1000,
		refetchOnWindowFocus: false,
		refetchInterval: 60 * 1000,
	});
};

export const useMatchGroupDetail = (groupId: string) => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	return useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_MATCH_GROUP_DETAIL,
			apiBackendInfo.url,
			groupId
		],
		queryFn: () => fetchMatchGroupDetail(apiBackendInfo, groupId),
		enabled: Boolean(siteConfig?.EnableGoldenDuel) && apiBackendInfo.isReady && !!groupId,
		cacheTime: 30 * 1000, // 30 seconds
		refetchOnWindowFocus: false
	});
};

export const useMatchGroupNextGame = (groupId: string) => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	return useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_MATCH_GROUP_NEXT_GAME,
			apiBackendInfo.url,
			groupId
		],
		queryFn: () => fetchMatchGroupNextGame(apiBackendInfo, groupId),
		enabled: Boolean(siteConfig?.EnableGoldenDuel) && apiBackendInfo.isReady && !!groupId,
		cacheTime: 30 * 1000, // 30 seconds
		refetchOnWindowFocus: false
	});
};
